<template>
  <a-drawer
    :title="dramaTitle"
    width="880"
    placement="right"
    :visible="visible"
    :closable="true"
    @close="onClose"
  >
    <a-table
      class="table-view"
      :data-source="dataSource"
      :columns="columns"
      :row-key="record => record.id"
      :pagination="pagination"
      :loading="isLoading"
      :scroll="{ y: 520 }"
      @change="handleTableChange"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'ad_episodes'">
          {{ formatEpisodeRange(record) }}
        </template>
        <template v-else-if="column.key === 'operation'">
          <a v-if="getStatusType(record) === 'ready'" class="op-link" @click.prevent="copyPromotionLink(record)">
            复制推广链接
          </a>
          <span v-else-if="getStatusType(record) === 'generating'" class="op-status">链接生成中</span>
          <span v-else-if="getStatusType(record) === 'failed'" class="op-status">链接生成失败</span>
          <span v-else class="op-status">-</span>
        </template>
        <template v-else>
          {{ record[column.dataIndex] ?? '-' }}
        </template>
      </template>
    </a-table>
  </a-drawer>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { message } from 'ant-design-vue'
import { dysFreeLink } from '@/api/copyright'

const visible = ref(false)
const isLoading = ref(false)
const dramaRecord = ref(null)
const dataSource = ref([])
const summary = ref({})

const pagination = reactive({
  total: 0,
  current: 1,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50', '100'],
  showTotal: total => `总共 ${total} 条`
})

const dramaTitle = computed(
  () => summary.value?.book_name || dramaRecord.value?.remark_name || dramaRecord.value?.book_name || '剧名'
)

const columns = [
  { title: '广告生效集数', key: 'ad_episodes', width: 160 },
  { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 120 },
  { title: '分销商', dataIndex: 'reseller_name', key: 'reseller_name', ellipsis: true },
  { title: '操作', key: 'operation', width: 140, align: 'center' }
]

function formatEpisodeRange(record) {
  const s = record.start_episode
  const e = record.total_ep
  if (s == null || e == null) return '-'
  return `第 ${s} - ${e} 集`
}

function normalizeRow(item) {
  return {
    ...item,
    created_at: item.created_date,
    reseller_name: item.company_name,
    promotion_url: item.advertise_link
  }
}

function getStatusType(record) {
  const hasLink = !!record.advertise_link
  if (hasLink) return 'ready'
  if (Number(record.status) === 1) return 'generating'
  return 'failed'
}

function loadList() {
  const record = dramaRecord.value || {}
  isLoading.value = true
  dataSource.value = []
  dysFreeLink({
    root_book_id: record.root_book_id,
    book_id: record.book_id,
    page: String(pagination.current),
    page_size: String(pagination.pageSize)
  })
    .then(res => {
      const { code, data } = res
      if (code === 0) {
        summary.value = data?.summary || {}
        dataSource.value = (data?.data || []).map(normalizeRow)
        pagination.total = data?.total || 0
      } else {
        message.error(res.message || '加载失败')
      }
    })
    .catch(() => {
      message.error('加载失败')
    })
    .finally(() => {
      isLoading.value = false
    })
}

function showDrawer(record) {
  dramaRecord.value = record || null
  pagination.current = 1
  visible.value = true
  loadList()
}

function onClose() {
  visible.value = false
}

function handleTableChange(p) {
  pagination.current = p.current
  pagination.pageSize = p.pageSize
  loadList()
}

function copyPromotionLink(record) {
  const url = record.promotion_url
  if (!url) {
    message.warning('暂无链接')
    return
  }
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url).then(
      () => message.success('已复制推广链接'),
      () => message.error('复制失败')
    )
  } else {
    message.error('当前环境不支持复制')
  }
}

defineExpose({ showDrawer })
</script>

<style scoped>
.op-link {
  color: #1890ff;
  cursor: pointer;
}
.op-status {
  color: rgba(0, 0, 0, 0.45);
}
</style>
